import { useTranslations } from "next-intl";
import { WalletAddress } from "@/components/core/WalletAddress/WalletAddress";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import type { components } from "@/types/api";

type TicketDto = components["schemas"]["NftPurchaseHistoryTicketDto"];

const STATUS_BADGE: Partial<
  Record<TicketDto["status"], { className: string }>
> = {
  EXPIRED: {
    className: "border-semantic-error text-semantic-error",
  },
};

function getDaysLeft(expiresAt: string | null): number | null {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

interface NFTHistoryCardWrapperProps {
  children: React.ReactNode;
  nftCollectionAddress: string;
  nftId: string;
  ticket?: TicketDto | null;
}

export const NFTHistoryCardWrapper = ({
  children,
  nftCollectionAddress,
  nftId,
  ticket,
}: NFTHistoryCardWrapperProps) => {
  const t = useTranslations("checkout");
  const badge = ticket ? STATUS_BADGE[ticket.status] : undefined;
  const daysLeft = ticket ? getDaysLeft(ticket.expires_at) : null;

  return (
    <div className="w-full max-w-100 space-y-2 p-3 border border-accent-secondary rounded-lg mx-auto">
      <div className="space-y-1">
        <h4 className="font-semibold text-lg">
          {t("nftHistory.contractAddress")}
        </h4>
        <WalletAddress
          className="text-sm text-text-secondary"
          address={nftCollectionAddress}
          truncateSize="full"
        />
      </div>
      {children}
      {ticket && (
        <>
          <Separator className="bg-stroke-subtle" />
          <div className="space-y-2 pt-1">
            {badge && (
              <div className="flex items-center justify-between">
                <span
                  className={cn(
                    "text-xs font-semibold border rounded-sm px-2 py-0.5",
                    badge.className,
                  )}
                >
                  {t("ticket.expired")}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between text-sm">
              <span className="text-text-secondary">
                {t("ticket.usageCount")}
              </span>
              <span className="font-semibold text-text-primary">
                {t("ticket.usageValue", {
                  used: ticket.used_person_trips,
                  total: ticket.total_person_trips,
                })}
              </span>
            </div>
            {daysLeft !== null && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-text-secondary">
                  {t("ticket.remainingDays")}
                </span>
                <span className="font-semibold text-text-primary">
                  {t("ticket.daysValue", { days: daysLeft })}
                </span>
              </div>
            )}
            <Button
              colors="secondary"
              size="sm"
              className="w-full mt-2"
              asChild
            >
              <Link href={`/fast-checkout/ticket-details/${nftId}`}>
                {t("nftHistory.viewTicket")}
              </Link>
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
